import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { DashboardStats } from "../services/dashboard/api";

type TableRow = Array<string | number>;

const formatNumber = (value: unknown) => {
  const numberValue = Number(value || 0);
  if (!Number.isFinite(numberValue)) return "0";

  const sign = numberValue < 0 ? "-" : "";
  const absoluteValue = Math.abs(numberValue);
  const hasDecimals = !Number.isInteger(absoluteValue);
  const [integerPart, decimalPart] = (hasDecimals
    ? absoluteValue.toFixed(2).replace(/0+$/, "").replace(/\.$/, "")
    : String(Math.round(absoluteValue))
  ).split(".");
  const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  return `${sign}${groupedInteger}${decimalPart ? `.${decimalPart}` : ""}`;
};

const text = (value: unknown, fallback = "-") => {
  const stringValue = String(value ?? "")
    .replace(/[\u00a0\u202f\u2007]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return stringValue || fallback;
};

const addSectionTitle = (doc: jsPDF, title: string, y: number) => {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(15, 23, 42);
  doc.text(title, 14, y);
  doc.setDrawColor(14, 116, 144);
  doc.line(14, y + 2, 196, y + 2);
};

const addTable = (
  doc: jsPDF,
  title: string,
  head: string[],
  body: TableRow[],
  startY: number,
  fontSize = 8,
) => {
  addSectionTitle(doc, title, startY);
  autoTable(doc, {
    startY: startY + 6,
    head: [head],
    body,
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize,
      cellPadding: 2,
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: [15, 118, 110],
      textColor: [255, 255, 255],
      fontStyle: "bold",
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    margin: { left: 14, right: 14 },
  });
  return (doc as any).lastAutoTable.finalY + 10;
};

const ensurePageSpace = (doc: jsPDF, y: number, needed = 45) => {
  if (y + needed <= 285) return y;
  doc.addPage();
  return 18;
};

export function generateNationalDashboardPdf(dashboard: DashboardStats) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const generatedAt = new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "long",
    timeStyle: "short",
  }).format(new Date());

  doc.setFillColor(0, 98, 51);
  doc.rect(0, 0, 210, 24, "F");
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 24, 210, 1.2, "F");
  doc.setFillColor(210, 16, 52);
  doc.rect(0, 25.2, 210, 1.2, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.text("AIDA - Rapport national de pilotage", 14, 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Genere le ${generatedAt}`, 14, 20);

  doc.setTextColor(51, 65, 85);
  doc.setFontSize(9);
  doc.text(
    "Synthese automatique basee sur les valeurs courantes du dashboard national.",
    14,
    34,
  );

  let y = 44;

  const kpiRows = [...(dashboard.kpis || []), ...(dashboard.secondaryKpis || [])].map(
    (kpi) => [text(kpi.label), formatNumber(kpi.value)],
  );
  y = addTable(doc, "1. Indicateurs nationaux", ["Indicateur", "Valeur"], kpiRows, y);

  y = ensurePageSpace(doc, y);
  y = addTable(
    doc,
    "2. Couverture de la base",
    ["Module", "Nombre", "Detail"],
    (dashboard.databaseCoverage || []).map((item) => [
      text(item.module),
      formatNumber(item.count),
      text(item.detail),
    ]),
    y,
  );

  y = ensurePageSpace(doc, y);
  y = addTable(
    doc,
    "3. Segments fournisseurs",
    ["Segment", "Fournisseurs"],
    (dashboard.supplierSegments || []).map((item) => [
      text(item.name),
      formatNumber(item.value),
    ]),
    y,
  );

  y = ensurePageSpace(doc, y);
  y = addTable(
    doc,
    "4. Capacite industrielle declaree",
    ["Filiere", "Part", "Produits", "Capacite"],
    (dashboard.industrialCapacity || []).map((item) => [
      text(item.name),
      `${formatNumber(item.value)}%`,
      formatNumber(item.count),
      formatNumber(item.capacity),
    ]),
    y,
  );

  y = ensurePageSpace(doc, y);
  y = addTable(
    doc,
    "5. Wilayas fournisseurs",
    ["Wilaya", "Fournisseurs"],
    (dashboard.topWilayas || []).map((item) => [
      text(item.name),
      formatNumber(item.value),
    ]),
    y,
  );

  y = ensurePageSpace(doc, y);
  y = addTable(
    doc,
    "6. Certifications fournisseurs",
    ["Certification", "Fournisseur", "Numero", "Expiration", "Statut", "Impact export"],
    (dashboard.certificationList || []).map((item) => [
      text(item.name),
      text(item.supplier),
      text(item.certificateNumber),
      text(item.expiryDate),
      text(item.status),
      text(item.exportImpact),
    ]),
    y,
  );

  y = ensurePageSpace(doc, y);
  y = addTable(
    doc,
    "7. Produits references prioritaires",
    ["Produit", "Fournisseur", "Categorie", "Capacite", "Unite"],
    (dashboard.topProducts || []).map((item) => [
      text(item.name),
      text(item.supplier),
      text(item.category),
      formatNumber(item.annualCapacity),
      text(item.unit),
    ]),
    y,
  );

  y = ensurePageSpace(doc, y);
  y = addTable(
    doc,
    "8. Filieres detaillees",
    ["Filiere", "Fourn.", "Produits", "Capacite", "Poles"],
    (dashboard.filiereStats || []).slice(0, 20).map((item) => [
      text(item.name),
      formatNumber(item.suppliers),
      formatNumber(item.products),
      formatNumber(item.capacity),
      text((item.poles || []).join(", ")),
    ]),
    y,
  );

  y = ensurePageSpace(doc, y);
  y = addTable(
    doc,
    "9. Liste complete des fournisseurs",
    [
      "ID",
      "Fournisseur",
      "Type",
      "Filiere",
      "Wilaya",
      "Statut",
      "Produits",
      "Certif.",
      "Capacite",
    ],
    (dashboard.allSuppliers || []).map((item) => [
      formatNumber(item.idSupplier),
      text(item.name),
      text(item.companyType || item.supplierType),
      text(item.industrialSector),
      text(item.wilaya),
      text(item.industrialStatus),
      formatNumber(item.products),
      formatNumber(item.certifications),
      formatNumber(item.capacity),
    ]),
    y,
    6.5,
  );

  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(`AIDA - Rapport national | Page ${page}/${pageCount}`, 14, 292);
  }

  doc.save(`AIDA_rapport_national_${new Date().toISOString().slice(0, 10)}.pdf`);
}
